import React, { useCallback, useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { unitsApi } from '../api/endpoints';
import { toast } from 'sonner';
import DeleteButton from './DeleteButton';

const EDITABLE_STATUSES = ['Available', 'Locked'];
const STATUS_TONE = {
  Available: 'text-emerald-700 border-emerald-300 bg-emerald-50',
  Locked: 'text-amber-700 border-amber-300 bg-amber-50',
  Booked: 'text-white border-brand-navy bg-brand-navy',
};

const emptyAdd = { unit_no: '', floor: '', type: '', area: '', price: '' };
const emptyBulk = { prefix: 'U', start_no: '1', count: '', type: '', area: '', price: '' };

const InventoryEditor = ({ projectId, onChanged }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceEdits, setPriceEdits] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);
  const [bulkForm, setBulkForm] = useState(emptyBulk);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await unitsApi.list({ project_id: projectId });
      setUnits(list.sort((a, b) => (a.unit_no || '').localeCompare(b.unit_no || '', undefined, { numeric: true })));
    } catch (e) { toast.error('Failed to load units'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); onChanged?.(); };

  const patchUnit = async (uid, patch, label) => {
    try {
      await unitsApi.update(uid, patch);
      toast.success(label);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const savePrice = (u) => {
    const raw = priceEdits[u.id];
    if (raw === undefined || raw === '' || Number(raw) === Number(u.price)) return;
    patchUnit(u.id, { price: Number(raw) }, `Price updated for ${u.unit_no}`);
    setPriceEdits(p => { const n = { ...p }; delete n[u.id]; return n; });
  };

  const deleteUnit = async (u) => {
    try {
      await unitsApi.remove(u.id);
      toast.success(`Unit ${u.unit_no} deleted`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const addUnit = async () => {
    if (!addForm.unit_no.trim()) { toast.error('Unit No is required'); return; }
    setSaving(true);
    try {
      await unitsApi.create({ project_id: projectId, ...addForm, price: Number(addForm.price) || 0 });
      toast.success(`Unit ${addForm.unit_no} added`);
      setAddForm(emptyAdd);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add unit'); }
    finally { setSaving(false); }
  };

  const bulkAdd = async () => {
    const count = Number(bulkForm.count);
    if (!count || count < 1) { toast.error('Enter how many units to create'); return; }
    setSaving(true);
    try {
      const r = await unitsApi.bulk({
        project_id: projectId, prefix: bulkForm.prefix || 'U',
        start_no: Number(bulkForm.start_no) || 1, count,
        type: bulkForm.type, area: bulkForm.area, price: Number(bulkForm.price) || 0,
      });
      toast.success(`${r.created} units created`);
      setBulkForm(emptyBulk);
      setShowBulk(false);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk add failed'); }
    finally { setSaving(false); }
  };

  const shown = statusFilter === 'all' ? units : units.filter(u => u.status === statusFilter);
  const counts = units.reduce((m, u) => { m[u.status] = (m[u.status] || 0) + 1; return m; }, {});

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="inventory-editor">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        <Icons.Grid3X3 className="w-3.5 h-3.5 text-brand-gold" /> Edit Inventory (Admin)
        <span className="ml-auto flex gap-1.5 normal-case font-normal">
          <Button size="sm" variant={showAdd ? 'secondary' : 'outline'} className="h-6 text-[10px] px-2" onClick={() => { setShowAdd(v => !v); setShowBulk(false); }} data-testid="inventory-add-unit-btn">
            <Icons.Plus className="w-3 h-3 mr-1" /> Add Unit
          </Button>
          <Button size="sm" variant={showBulk ? 'secondary' : 'outline'} className="h-6 text-[10px] px-2" onClick={() => { setShowBulk(v => !v); setShowAdd(false); }} data-testid="inventory-bulk-add-btn">
            <Icons.Layers className="w-3 h-3 mr-1" /> Bulk Add
          </Button>
        </span>
      </div>
      <div className="p-3 space-y-2">
        {showAdd && (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-1.5 items-end bg-slate-50 border border-slate-200 rounded-md p-2" data-testid="inventory-add-form">
            {[['unit_no', 'Unit No*'], ['floor', 'Floor'], ['type', 'Type'], ['area', 'Area'], ['price', 'Price ₹']].map(([k, l]) => (
              <div key={k}>
                <div className="text-[9px] uppercase text-slate-500 mb-0.5">{l}</div>
                <Input className="h-7 text-xs" type={k === 'price' ? 'number' : 'text'} value={addForm[k]}
                  onChange={e => setAddForm(f => ({ ...f, [k]: e.target.value }))} data-testid={`inventory-add-${k}`} />
              </div>
            ))}
            <Button size="sm" className="h-7 text-xs bg-brand-navy" disabled={saving} onClick={addUnit} data-testid="inventory-add-save-btn">Save</Button>
          </div>
        )}
        {showBulk && (
          <div className="grid grid-cols-2 sm:grid-cols-7 gap-1.5 items-end bg-slate-50 border border-slate-200 rounded-md p-2" data-testid="inventory-bulk-form">
            {[['prefix', 'Prefix'], ['start_no', 'Start No'], ['count', 'Count*'], ['type', 'Type'], ['area', 'Area'], ['price', 'Price ₹']].map(([k, l]) => (
              <div key={k}>
                <div className="text-[9px] uppercase text-slate-500 mb-0.5">{l}</div>
                <Input className="h-7 text-xs" type={['start_no', 'count', 'price'].includes(k) ? 'number' : 'text'} value={bulkForm[k]}
                  onChange={e => setBulkForm(f => ({ ...f, [k]: e.target.value }))} data-testid={`inventory-bulk-${k}`} />
              </div>
            ))}
            <Button size="sm" className="h-7 text-xs bg-brand-navy" disabled={saving} onClick={bulkAdd} data-testid="inventory-bulk-save-btn">Create</Button>
          </div>
        )}

        <div className="flex items-center gap-1.5 flex-wrap">
          {['all', 'Available', 'Locked', 'Booked'].map(s => (
            <button key={s} type="button" onClick={() => setStatusFilter(s)} data-testid={`inventory-filter-${s}`}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${statusFilter === s ? 'bg-brand-navy text-white border-brand-navy' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              {s === 'all' ? `All (${units.length})` : `${s} (${counts[s] || 0})`}
            </button>
          ))}
        </div>

        {loading ? <div className="text-xs text-slate-400 py-3 text-center">Loading units…</div> : shown.length === 0 ? (
          <div className="text-xs text-slate-400 py-3 text-center">No units — use Add Unit or Bulk Add above.</div>
        ) : (
          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-slate-100 rounded-md">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white shadow-sm">
                <tr className="text-left text-[10px] uppercase text-slate-400">
                  <th className="py-1.5 pl-2">Unit</th><th>Floor</th><th>Type</th><th>Area</th><th>Price ₹</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map(u => (
                  <tr key={u.id} data-testid={`inventory-row-${u.unit_no}`}>
                    <td className="py-1 pl-2 font-semibold">{u.unit_no}</td>
                    <td className="text-slate-500">{u.floor || '—'}</td>
                    <td className="text-slate-500">{u.type || '—'}</td>
                    <td className="text-slate-500">{u.area || '—'}</td>
                    <td>
                      <Input className="h-6 w-24 text-xs px-1.5" type="number" disabled={u.status === 'Booked'}
                        value={priceEdits[u.id] ?? u.price ?? 0}
                        onChange={e => setPriceEdits(p => ({ ...p, [u.id]: e.target.value }))}
                        onBlur={() => savePrice(u)}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        data-testid={`inventory-price-${u.unit_no}`} />
                    </td>
                    <td>
                      {u.status === 'Booked' ? (
                        <Badge variant="outline" className={`text-[9px] ${STATUS_TONE.Booked}`}>Booked</Badge>
                      ) : (
                        <Select value={u.status} onValueChange={v => patchUnit(u.id, { status: v }, `${u.unit_no} → ${v}`)}>
                          <SelectTrigger className={`h-6 w-[100px] text-[10px] border rounded-full px-2 ${STATUS_TONE[u.status] || ''}`} data-testid={`inventory-status-${u.unit_no}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {EDITABLE_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="pr-1 text-right" data-testid={`inventory-delete-${u.unit_no}`}>
                      <DeleteButton
                        disabled={u.status === 'Booked'}
                        title={`Delete unit ${u.unit_no}?`}
                        description="This unit will be permanently removed from the project inventory."
                        onConfirm={() => deleteUnit(u)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryEditor;
